import type { ReactElement } from 'react'
import React, { useState } from 'react'
import { PopupModal, useCalendlyEventListener } from 'react-calendly'
import { Platform } from 'react-native'
import { P, isMatching, match } from 'ts-pattern'
import { z } from 'zod'

import { useFlowsServicesResponses } from '@flows-ui/services/FlowServicesResponsesContext'
import { useTranslation } from '@flows-ui/utils/i18n'
import { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import { BUTTON_IDS, EVENTS } from '@flows/configs/analytics/constants'
import type { Calendly as CalendlyProps } from '@flows/core/types/stepContentTypes'
import type { FlowValues } from '@flows/core/types/stepTypes'
import * as calendarIcon from '@ui-kit/assets/images/src/icons/large/calendar_64.svg'
import * as chevronRight from '@ui-kit/assets/images/src/icons/small/action/chevronRight_24.svg'
import CallToActionCard from '@ui-kit/components/cards/CallToActionCard'

export const Calendly = <TProps extends CalendlyProps>(
  props: TProps & {
    flowValues: FlowValues
    children?: ReactElement
    track: (event: string, properties?: Record<string, any>) => void
    flowName: string
    onSubmit?: (args: { eventUrl: string }) => Promise<void> | void
  }
) => {
  const { t } = useTranslation()
  const servicesResponses = useFlowsServicesResponses()
  const translateFunction = processStepContentElementText(t, {
    flowValues: props.flowValues,
    servicesResponses,
  })
  const title = translateFunction(props.title)
  const subtitle = props.subtitle ? translateFunction(props.subtitle) : undefined
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false)
  const utmMedium = Platform.select({
    web: 'web',
    default: 'app',
  })
  const utmSource = Platform.select({
    web: props.flowValues.utm_source ?? 'web_funnel_bu',
    default: 'product_card_bu',
  })
  const utmCampaign = props.flowValues.utm_campaign ?? 'appointment_booked_self'
  const link = new URL(props.link)
  link.searchParams.set('utm_medium', utmMedium)
  const isString = isMatching(P.string)
  if (isString(utmSource)) {
    link.searchParams.set('utm_source', utmSource)
  }
  if (isString(utmCampaign)) {
    link.searchParams.set('utm_campaign', utmCampaign)
  }

  const name = match(props.flowValues)
    .with({ first_name: P.string, last_name: P.string }, ({ first_name, last_name }) => {
      return `${first_name} ${last_name}`
    })
    .otherwise(() => undefined)

  const prefill = {
    name,
    email: z.string().parse(props.flowValues.email),
    customAnswers: {
      a1: z.string().parse(props.flowValues.phone_number),
    },
  }

  useCalendlyEventListener({
    onEventTypeViewed: () =>
      props.track(EVENTS.MGABookingCalendlyPageOpened, {
        flowName: props.flowName,
      }),
    onDateAndTimeSelected: () =>
      props.track(EVENTS.MGABookingCalendlyDateAndTimeSelected, {
        flowName: props.flowName,
      }),
    onEventScheduled: async (event) => {
      if (props.onSubmit) {
        await props.onSubmit({ eventUrl: event.data?.payload?.event?.uri })
      }
      setIsCalendlyOpen(false)
      props.track(EVENTS.MGABookingCalendlyAppointmentScheduled, {
        calendlyEventUrl: event.data?.payload?.event?.uri,
        flowName: props.flowName,
      })
    },
  })

  return (
    <>
      <PopupModal
        url={link.toString()}
        prefill={prefill}
        rootElement={
          (typeof window !== 'undefined' ? document.getElementById('__next') : null) as HTMLElement
        }
        onModalClose={() => {
          setIsCalendlyOpen(false)
        }}
        open={isCalendlyOpen}
      />
      <CallToActionCard
        icon={<img alt="" src={calendarIcon.default.src} />}
        actionIcon={<img alt="" src={chevronRight.default.src} />}
        bgColor={props.bgColor}
        title={title}
        subtitle={subtitle}
        onPress={() => {
          props.track(EVENTS.ButtonClicked, {
            buttonId: BUTTON_IDS.CalendlyScheduleAppointment,
            flowName: props.flowName,
            type: 'link',
            href: link,
          })
          setIsCalendlyOpen(true)
        }}
      />
    </>
  )
}
